// AUTO-GENERATED by scripts/extract-tool-catalog.mjs — do not edit manually
// Run: node scripts/extract-tool-catalog.mjs

export interface ToolParam {
  name: string
  type: string
  description: string
  required: boolean
  enum?: string[]
}

export interface CatalogTool {
  name: string
  category: string
  description: string
  isBuiltIn?: boolean
  params: ToolParam[]
}

export const TOOL_CATALOG: CatalogTool[] = [
  {
    "name": "web_search",
    "category": "Search",
    "description": "Server-side web search (Anthropic built-in tool)",
    "isBuiltIn": true,
    "params": []
  },
  {
    "name": "set_title",
    "category": "Project",
    "description": "Set or update the project title. Call this early — as soon as you have any sense of the topic, even before full direction is clear. You can always call it again later to refine the title.",
    "params": [
      {
        "name": "title",
        "type": "string",
        "description": "A catchy 3-word title.",
        "required": true
      }
    ]
  },
  {
    "name": "read_url_content",
    "category": "Search",
    "description": "Read a URL and return cleaned page content. Use this when the user provides a URL or domain so plans are grounded in the actual page. If crawl is enabled, the system also discovers same-site images, auto-ingests them, and returns their asset IDs and metadata.",
    "params": [
      {
        "name": "url",
        "type": "string",
        "description": "HTTP(S) URL to read.",
        "required": true
      },
      {
        "name": "crawl",
        "type": "boolean",
        "description": "If true, crawl same-site links up to two levels and return discovered image assets (asset_id, image_name, dimensions, file_size).",
        "required": false
      }
    ]
  },
  {
    "name": "update_project_settings",
    "category": "Other",
    "description": "Update project-level settings like aspect ratio, default clip duration, or preferred models. Call this early when the user specifies a format (portrait, landscape, square) or when changing project defaults.",
    "params": [
      {
        "name": "aspect_ratio",
        "type": "string",
        "description": "Project aspect ratio. Use 9:16 for portrait/vertical/mobile/TikTok/Reels/Shorts, 16:9 for landscape/widescreen (default), 1:1 for square/Instagram.",
        "required": false,
        "enum": [
          "16:9",
          "9:16",
          "1:1"
        ]
      },
      {
        "name": "default_duration",
        "type": "number",
        "description": "Default clip duration in seconds (3-15).",
        "required": false
      },
      {
        "name": "video_model",
        "type": "string",
        "description": "Preferred video generation model ID.",
        "required": false
      }
    ]
  },
  {
    "name": "update_narrative",
    "category": "Project",
    "description": "Update the narrative document. Use this to build and refine the story collaboratively with the user. IMPORTANT: Never include the project title in the content — the title is already displayed separately above the narrative in the UI, so including it creates an ugly duplicate.",
    "params": [
      {
        "name": "content",
        "type": "string",
        "description": "The full narrative content (replaces existing). MUST NOT begin with or contain the project title — it's shown separately. Start directly with the story description (e.g. 'A short film about...'). Any runtime or scene timestamps stated in the content must reflect the actual timeline once clips exist — not the planning-time estimates.",
        "required": true
      }
    ]
  },
  {
    "name": "update_entities",
    "category": "Other",
    "description": "Add or update the project's reusable subjects — characters, locations, products, and elements. Set entity_type to choose which, and pass the entities array. Upserts by id: pass only the entities you're adding or changing; ones you omit are preserved untouched.\n\n- character: a person/creature who appears on screen. MUST have exhaustive visual details — these descriptions are auto-injected into every scene's video_prompt. May carry voice_id.\n- location: a setting/place, first-class like characters. Vivid self-contained description (interior/exterior, era, time of day, light, key set dressing). No people. Scout with generate_references entity_type='location'.\n- product: a hero product/app. Precise visual description (shape, materials, label/logo text verbatim, colors; for apps the key screen and its UI). No people. When the user uploaded real product shots, use those directly — never generate a substitute. Generate refs with generate_references entity_type='product'.\n- element: any other recurring subject (mascot, pet, vehicle, hero prop, logo, wardrobe, artifact). Requires kind. Same lifecycle: define, generate options if no imagery, lock the pick, reuse. When it exists in real life, request real assets first.",
    "params": [
      {
        "name": "entity_type",
        "type": "string",
        "description": "Which kind of entity these are.",
        "required": false,
        "enum": [
          "character",
          "location",
          "product",
          "element"
        ]
      },
      {
        "name": "entities",
        "type": "array",
        "description": "Unique identifier for the entity.",
        "required": false
      }
    ]
  },
  {
    "name": "generate_references",
    "category": "Other",
    "description": "Generate reference image options for any entity — character, location, product, or element. Set entity_type to choose which, and entity_id to point at it. The locked reference anchors that subject's look across every clip and composite (and is REQUIRED before generate_clip_video mode=\"reference-to-video\"). Returns asset_ids in the response and stores them in the entity's context.\n\nPattern is the same for all four: define the entity (update_entities) → generate 3-6 distinct option takes → present via ask_questions with one recommended → user picks → call again with selected_asset_id to lock it. Skip the choice round (single option, no question) when autonomy is surprise_me or the user told you to proceed.\n\n- character (casting): each option is a DIFFERENT person who could play the role. If it could plausibly be a real person (the user, a founder), first ask whether they can upload a photo. Use variation_briefs for the casting round (genuinely different people, not adjective swaps). After the user picks, call again with selected_asset_id + poses=[\"three_quarter\"] to finalize the views, then rewrite the character description (update_entities) to match. Use asset_ids with generate_clip_video mode=\"reference-to-video\".\n- location (scouting): wide establishing plates, empty of people. Vary light, angle-feel, dressing, mood.\n- product (design): when the user has NO real product images. Vary packaging form, label layout, color story (or for apps the screen layout/style). SKIP entirely when real product imagery exists — real always beats generated.\n- element: same as product/location for mascots, pets, vehicles, props, logos.\n\nFor location/product/element, use option_briefs (3-4 distinct standalone takes); the chosen option becomes the entity, so update its description to match after selection. SKIP SELECTION when the user already provided real imagery or asked you to go ahead.",
    "params": [
      {
        "name": "entity_type",
        "type": "string",
        "description": "Which kind of entity to generate references for.",
        "required": false,
        "enum": [
          "character",
          "location",
          "product",
          "element"
        ]
      },
      {
        "name": "entity_id",
        "type": "string",
        "description": "ID of the entity (the character/location/product/element) to generate references for.",
        "required": false
      },
      {
        "name": "option_briefs",
        "type": "array",
        "description": "One brief per option (character: 3-6; location/product/element: 3-4). Each a complete, self-contained visual description in the project's art style of a distinct take. For character these are casting briefs (a different person each: age, build, face, wardrobe, energy). The chosen option becomes the entity — update its description to match after selection.",
        "required": false
      },
      {
        "name": "num_options",
        "type": "number",
        "description": "How many takes to generate (1-6) when not passing option_briefs. Prefer option_briefs — it produces far more distinct options.",
        "required": false
      },
      {
        "name": "selected_asset_id",
        "type": "string",
        "description": "Asset ID of a previously generated option to lock as the entity's reference. For character, additional poses are then generated to match; for others it simply locks the pick.",
        "required": false
      },
      {
        "name": "poses",
        "type": "array",
        "description": "character only — specific poses to generate. Defaults to ['front']. Options: 'front' (facing camera), 'three_quarter' (3/4 view), 'profile' (side view), 'action' (dynamic pose).",
        "required": false
      }
    ]
  },
  {
    "name": "update_brief",
    "category": "Other",
    "description": "Record creative-brief decisions on the project — the structured source of truth for pre-production. Merge-patch: only fields you pass change. Write every decision here as it lands (from intake answers or your own calls), so casting, location scouting, sound, and clip prompts all read one consistent brief, and the treatment board renders it.\n\nThe brief is elastic, not a fixed form. The named fields cover the universal dimensions; for everything specific to THIS project, add \\",
    "params": [
      {
        "name": "autonomy",
        "type": "string",
        "description": "How much the user wants to be consulted",
        "required": false,
        "enum": [
          "direct",
          "key_decisions",
          "surprise_me"
        ]
      },
      {
        "name": "objective",
        "type": "string",
        "description": "What the video is for (sell, explain, entertain, announce...)",
        "required": false
      },
      {
        "name": "audience",
        "type": "string",
        "description": "Who it's aimed at and where it plays",
        "required": false
      },
      {
        "name": "format",
        "type": "string",
        "description": "Platform + length intent, e.g. 'TikTok vertical, ~60s'",
        "required": false
      },
      {
        "name": "tone",
        "type": "string",
        "description": "Emotional register / writing voice",
        "required": false
      },
      {
        "name": "look",
        "type": "string",
        "description": "Visual medium, lighting, camera language, how real it should feel",
        "required": false
      },
      {
        "name": "palette",
        "type": "array",
        "description": "3-5 hex colors defining the grade/palette",
        "required": false
      },
      {
        "name": "world",
        "type": "string",
        "description": "Setting, era, season, time of day",
        "required": false
      },
      {
        "name": "sound",
        "type": "string",
        "description": "Music + voice direction",
        "required": false
      },
      {
        "name": "typography",
        "type": "string",
        "description": "Caption / text style direction",
        "required": false
      },
      {
        "name": "references",
        "type": "string",
        "description": "Creative touchstones ('like a Nike ad', 'A24 trailer')",
        "required": false
      },
      {
        "name": "avoid",
        "type": "string",
        "description": "The anti-brief: what this must NOT be or look like (clichés to dodge, competitor looks, 'don't look AI'). Feeds negative prompts and steers every choice.",
        "required": false
      },
      {
        "name": "product",
        "type": "string",
        "description": "The hero object/app and how it's shown: held, used, unboxed, beauty shot; real images vs generated references",
        "required": false
      },
      {
        "name": "hook",
        "type": "string",
        "description": "First-3-seconds strategy: pattern interrupt, bold claim, visual surprise...",
        "required": false
      },
      {
        "name": "ending",
        "type": "string",
        "description": "How it ends: seamless loop, logo end card, CTA mechanics ('link in bio', promo code)",
        "required": false
      },
      {
        "name": "have_assets",
        "type": "array",
        "description": "What the user already has (product shots, brand kit, script, footage...)",
        "required": false
      },
      {
        "name": "logline",
        "type": "string",
        "description": "One-line pitch for the video",
        "required": false
      },
      {
        "name": "custom",
        "type": "array",
        "description": "Stable slug, e.g. 'recipe-arc'",
        "required": false
      }
    ]
  },
  {
    "name": "request_assets",
    "category": "Other",
    "description": "Ask the user for their real files — product shots, photos of their pet/car/storefront, logo, app screenshots, a voice memo, existing footage, a reference video. Renders an upload card listing exactly what you need; each item has an upload button, a \"generate it instead\" fallback, and the card can be skipped.\n\nReal assets beat generated ones for anything that exists in the user's real life — request before you generate. Use right after the intake's \"what do you have\" answer, or whenever a planned shot depends on something they likely have. Don't block on it: if they skip or go quiet, proceed with generation. Skip requesting entirely when autonomy is surprise_me.",
    "params": [
      {
        "name": "requests",
        "type": "array",
        "description": "1-6 specific asks",
        "required": false,
        "enum": [
          "image",
          "video",
          "audio",
          "document",
          "any"
        ]
      },
      {
        "name": "note",
        "type": "string",
        "description": "One optional line of context above the requests",
        "required": false
      }
    ]
  },
  {
    "name": "show_treatment",
    "category": "Other",
    "description": "Present the treatment board — a single visual summary of every creative decision (brief, cast, world, product, look, palette, sound, type, beats) rendered live from project state. This is the greenlight gate: show it after clips are created but BEFORE the first generate_clip_video fan-out, then wait for the user's go.\n\nThe board speaks for itself — never restate its contents (shot lists, audio plans, settings) as text around it. One short line of context at most.\n\nThe user responds by greenlighting (proceed to generation immediately, no further confirmation) or by saying what to change. Tiles on the board are clickable — adjustment requests arrive as messages like \"Change the setting: warmer light\" — treat them as targeted change requests, apply them, and the board updates live (no need to re-show it).\n\nSkip the gate — call show_treatment but proceed without waiting — when autonomy is surprise_me or the user already told you to generate. The board then serves as a receipt of what you decided.",
    "params": [
      {
        "name": "logline",
        "type": "string",
        "description": "One-line pitch shown under the title",
        "required": true
      },
      {
        "name": "cover_asset_id",
        "type": "string",
        "description": "Asset ID of the cover image — ideally the character-in-setting composite",
        "required": false
      },
      {
        "name": "notes",
        "type": "string",
        "description": "Anything the user should know before greenlighting (tradeoffs, open questions)",
        "required": false
      }
    ]
  },
  {
    "name": "create_clip",
    "category": "Timeline",
    "description": "Create a new clip (atomic video unit).\n\nVIDEO PROMPT REQUIREMENTS (CRITICAL):\nThe video_prompt is sent DIRECTLY to the video model with NO OTHER CONTEXT.\nThe model has NO knowledge of your story, other clips, or character names.\n\nNEVER USE:\n- \"continues from previous clip\" / \"as before\" / \"earlier\"\n- \"same outfit as clip 1\" / \"returns to the location\"\n- ANY reference to other clips or temporal relationships\n\nALWAYS:\n- Describe the COMPLETE clip as if it's the only one\n- Reference characters BY NAME (their descriptions are auto-injected)\n- Include full environment/setting details\n- Describe specific physical actions, not emotions\n- Write like a director giving shot instructions, not describing a photograph\n\nSTRUCTURE (200-400 words) - MUST include ALL:\n\n[CAMERA] - Shot type, lens, movement RELATIVE TO SUBJECT, and angle.\n           Use real cinematography language: dolly push, whip-pan, crash zoom, steadicam drift,\n           crane shot, FPV, truck left/right, rack focus, Dutch angle, low-angle tracking.\n           CRITICAL: Always describe movement relative to subject AND specify an endpoint.\n           BAD: \"tracking shot\" / \"camera moves right\"\n           GOOD: \"Camera tracks alongside Marcus at shoulder height as he walks down the corridor,\n                  then slows to a static medium shot as he reaches the door.\"\n           Open-ended camera movement with no endpoint causes generation artifacts and hangs.\n           When a character's likeness comes from reference images, choose an angle that keeps\n           their face visible — references can't preserve a face the camera never shows, and\n           keyframe chaining carries a turned-away pose into the next clip.\n\n[ACTION] - Specific physical movements with temporal structure. Keep simple per clip — split\n           complex sequences across clips (within the user's clip count, if they set one).\n           Use sequential beats: \"First [A], then [B], finally [C].\"\n           Describe PHYSICS: impact reactions, debris, fabric movement, hair in wind, splashes.\n           CRITICAL: For avatar clips or clips with TTS voiceover, NEVER put spoken words,\n           quotes, or speech attribution in [ACTION]. Describe only PHYSICAL motion (gestures,\n           expressions, body language). BAD: \"He says 'hello'\" / \"She whispers the secret\"\n           GOOD: \"He leans forward with a wry smirk, one eyebrow raised, gesturing loosely\"\n           BAD: \"She walks sadly to the window\"\n           GOOD: \"Maya takes three slow steps toward window, shoulders hunched, coat swaying\n                  with each step. Pauses, raises right hand, presses palm flat against glass.\n                  Condensation forms around her fingers.\"\n           All motion must have clear endpoints — never leave actions open-ended.\n\n[SETTING] - Complete environment with spatial grounding\n            BAD: \"The same kitchen\"\n            GOOD: \"Cramped 1970s kitchen, avocado-green appliances, Formica counters,\n                   morning light through yellowed curtains, steam rising from a kettle on the stove\"\n\n[LIGHTING] - Specific sources and direction: \"Soft diffused daylight from window camera-left,\n             warm desk lamp adds orange rim lighting on hair, deep shadows under cabinets\"\n             Use precise terminology: golden hour, hard shadows, volumetric light, practical lighting,\n             motivated key light, fill bounce, rim/edge light, silhouette.\n\n[SOUND] - Environment audio (specific sources), character sounds (breathing, footsteps).\n          Speech follows the clip's speech setting (see SPEECH MODEL):\n          - speech='voiceover' or 'none' (b-roll, scenery, montage, no visible speaker):\n            Add \"no dialog, no speech, silent scene\" — the floating voiceover track carries any words.\n          - speech='on_camera', native path: include the line as [Character, tone]: \"The dialog.\"\n            Do NOT add \"no dialog\" — this clip's own voice is the dialog source.\n          - speech='on_camera', avatar path: lip-synced from the TTS audio. Omit the spoken words from the prompt.\n\n[MOOD] - Color palette, film reference, atmosphere, emotional temperature\n\nDURATION GUIDANCE:\nStandard clip durations are 4, 6, or 8 seconds. Always use one of these values unless using multi-shot.\n- 4s: Single beat — one camera move, one action, a reaction shot\n- 6s: Two beats — an action sequence or short exchange\n- 8s: Extended — unfolding action with camera development\n- Multi-shot (up to 15s): Use shots array for 2-6 internal shots with distinct prompts per shot. Individual shot durations can be 3-15s.\nWhen audio drives timing, split the audio segment across multiple clips at 4/6/8s each rather than using non-standard durations.\n\nIMAGE-TO-VIDEO PROMPTS:\nWhen the clip has a start keyframe, the image is the visual anchor.\nFocus on HOW THE SCENE EVOLVES — motion, animation, changes — not what's already visible.\nBAD: \"A woman standing in a kitchen with morning light\" (re-describes the frame)\nGOOD: \"She reaches for the kettle, steam curling upward as morning light shifts across the counter\"\n\nCHARACTER AUTO-INJECTION:\nWhen you set characters_in_clip, system prepends their full descriptions.\nDO use character names for actions. DON'T repeat physical descriptions.",
    "params": [
      {
        "name": "order_index",
        "type": "number",
        "description": "Position in timeline (1-indexed). Clip 1 = 1, Clip 2 = 2, etc.",
        "required": false
      },
      {
        "name": "title",
        "type": "string",
        "description": "Short clip title",
        "required": false
      },
      {
        "name": "description",
        "type": "string",
        "description": "Brief description of what happens",
        "required": false
      },
      {
        "name": "video_prompt",
        "type": "string",
        "description": "Self-contained prompt (200-400 words) with [CAMERA], [ACTION], [SETTING], [LIGHTING], [SOUND], [MOOD]. Camera movement must be relative to subject with clear endpoints. All motion must have endpoints.",
        "required": false
      },
      {
        "name": "duration",
        "type": "number",
        "description": "Generation duration — must be 4, 6, or 8 seconds. See DURATION GUIDANCE.",
        "required": false
      },
      {
        "name": "characters_in_clip",
        "type": "array",
        "description": "Character IDs - their descriptions are auto-injected into video generation",
        "required": false
      },
      {
        "name": "is_jump_cut",
        "type": "boolean",
        "description": "TRUE if previous frame can't animate into this clip (location change, interior/exterior switch, time jump). FALSE if camera movement connects them naturally.",
        "required": false
      },
      {
        "name": "scene_label",
        "type": "string",
        "description": "Grouping label (e.g., 'Act 1', 'Chase Sequence')",
        "required": false
      },
      {
        "name": "speech",
        "type": "string",
        "description": "Where THIS clip's dialog comes from — decide by what the viewer sees. 'on_camera': a character visibly speaks the line to camera (UGC, testimonial, direct address, interview) → the clip MUST be lip-synced; also set dialog[] and generate with mode='avatar' or audio=true, never silent. 'voiceover': words are spoken but we don't see the mouth saying them (b-roll, cutaways, montage) → silent video + a floating voiceover track. 'none': no speech. See the SPEECH MODEL section.",
        "required": false,
        "enum": [
          "on_camera",
          "voiceover",
          "none"
        ]
      },
      {
        "name": "dialog",
        "type": "array",
        "description": "Character ID of the speaker",
        "required": false
      },
      {
        "name": "shots",
        "type": "array",
        "description": "Self-contained prompt for this shot (same requirements as video_prompt)",
        "required": false
      },
      {
        "name": "solid_color",
        "type": "string",
        "description": "Hex color (e.g. '#000000') for a solid color frame. When set, no video generation is needed — the clip renders as a flat color fill. Useful for black frames, fade separators, or color cards between scenes.",
        "required": false
      },
      {
        "name": "replaces_clip_id",
        "type": "string",
        "description": "ID of an existing clip this new clip replaces. The old clip is deleted atomically when the new one is created. Use when rebuilding or restructuring a section — avoids leaving orphaned clips behind.",
        "required": false
      }
    ]
  },
  {
    "name": "update_clip",
    "category": "Timeline",
    "description": "Update an existing clip. Only provided fields are updated. Changing video_prompt on a clip with completed video automatically marks it STALE — the video no longer matches the prompt until regenerated.",
    "params": [
      {
        "name": "clip_id",
        "type": "string",
        "description": "ID of the clip to update",
        "required": false
      },
      {
        "name": "title",
        "type": "string",
        "description": "Short clip title",
        "required": false
      },
      {
        "name": "description",
        "type": "string",
        "description": "Brief description of what happens",
        "required": false
      },
      {
        "name": "video_prompt",
        "type": "string",
        "description": "Self-contained prompt (200-400 words) with [CAMERA], [ACTION], [SETTING], [LIGHTING], [SOUND], [MOOD]. Camera movement must be relative to subject with clear endpoints. For i2v clips, focus on motion/evolution from the keyframe, not re-describing it.",
        "required": false
      },
      {
        "name": "duration",
        "type": "number",
        "description": "Generation duration — must be 4, 6, or 8 seconds.",
        "required": false
      },
      {
        "name": "characters_in_clip",
        "type": "array",
        "description": "Array of character IDs",
        "required": false
      },
      {
        "name": "is_jump_cut",
        "type": "boolean",
        "description": "TRUE if previous frame can't animate into this clip (location change, interior/exterior switch, time jump). FALSE if camera movement connects them naturally.",
        "required": false
      },
      {
        "name": "scene_label",
        "type": "string",
        "description": "Optional grouping label",
        "required": false
      },
      {
        "name": "speech",
        "type": "string",
        "description": "Where this clip's dialog comes from. 'on_camera' (visible speaker → must lip-sync: avatar or audio=true, never silent), 'voiceover' (off-screen words → silent video + floating track), 'none'. See SPEECH MODEL.",
        "required": false,
        "enum": [
          "on_camera",
          "voiceover",
          "none"
        ]
      },
      {
        "name": "stale_reasons",
        "type": "array",
        "description": "Set stale reasons for this clip. Pass empty array to clear staleness. Use when a dependency has changed and this clip's video is outdated.",
        "required": false
      },
      {
        "name": "dialog",
        "type": "array",
        "description": "Character ID of the speaker",
        "required": false
      }
    ]
  },
  {
    "name": "delete_clips",
    "category": "Timeline",
    "description": "Delete one or more clips from the timeline. DESTRUCTIVE: for any bulk deletion of completed work (more than a stray clip or two), or any deletion that is part of a rebuild/redo/clean-up flow, you MUST first surface the plan and obtain explicit user confirmation before calling this tool. Do not treat 'rebuild' or 'redo' as implicit consent.",
    "params": [
      {
        "name": "clip_ids",
        "type": "array",
        "description": "Array of clip IDs to delete",
        "required": true
      }
    ]
  },
  {
    "name": "edit_timeline",
    "category": "Other",
    "description": "Apply a batch of post-production edits to the timeline as an ordered list of operations. Each op has its own `op` type and fields; ops run in array order and are reported individually, so a later op can build on an earlier one. If one op fails, the ones before it stay applied — the result lists each op's outcome.\n\nOPS:\n- reorder: change clip order. clip_order must list ALL clip IDs in the desired order — any omitted clip is appended at the end in its original order. (Timeline-wide; pass one reorder op, not one per clip.)\n- trim: set non-destructive in/out points on a clip. trim_start/trim_end are seconds removed from each end (0 clears). Applied at export.\n- set_speed: change a clip's playback speed. 1.0 = normal, 0.5 = slow-mo, 2.0 = fast forward (range 0.25-4.0).\n- set_transition: set how a clip enters from the previous one. type='cut' removes any existing transition. gl_* types are GPU-accelerated WebGL transitions.",
    "params": [
      {
        "name": "operations",
        "type": "array",
        "description": "Ordered list of edit operations. Applied in sequence; each is reported separately.",
        "required": false,
        "enum": [
          "reorder",
          "trim",
          "set_speed",
          "set_transition"
        ]
      }
    ]
  },
  {
    "name": "set_clip_keyframe",
    "category": "Timeline",
    "description": "Set a START or END keyframe for a clip. Returns the asset_id.\n\nKEYFRAMES vs CHARACTER REFERENCES:\n- **Keyframes**: Literal START/END frames sent to the video model. Controls exact scene composition. image-to-video requires a start keyframe; an end keyframe is optional (set both for controlled start→end transitions). An end keyframe alone cannot drive generation.\n- **Character refs**: Appearance guidance only. Do NOT use as keyframes.\n\nSOURCES (prefer in this order):\n- \"asset\": Use an existing asset (uploaded image, crawled image, generate_image output, etc.). Preserves visual fidelity — always prefer this when you have a relevant image. Also the best way to create continuity: use the same asset as clip N's end keyframe AND clip N+1's start keyframe.\n- \"adjacent_clip\": Chain from neighboring clip's actual rendered video frame. For \"start\" uses previous clip's end frame; for \"end\" uses next clip's start frame.\n- \"generate\": Create a new frame from a text prompt. Use when no existing visual asset fits the clip. If you have a relevant asset but need it recomposed, use generate_image with input_image (img2img) first, then set the result as source=\"asset\" — closer to the source than text alone, but still a full repaint that alters text and fine detail. If the asset works as-is, skip img2img and use source=\"asset\" directly.\n\nWHEN TO USE:\n- Clip 1 with specific composition needs\n- Jump cuts where you need control over the new scene's first frame\n- When you want start→end frame interpolation (set BOTH keyframes, then mode=\"image-to-video\")\n- Every ~5 clips to prevent visual drift\n- For continuity: generate one image, share it as clip N end + clip N+1 start (via source=\"asset\")\n\nFree, no usage charged.",
    "params": [
      {
        "name": "clip_id",
        "type": "string",
        "description": "ID of clip",
        "required": true
      },
      {
        "name": "position",
        "type": "string",
        "description": "Which keyframe position: start or end",
        "required": true,
        "enum": [
          "start",
          "end"
        ]
      },
      {
        "name": "source",
        "type": "string",
        "description": "Source: 'generate' = create new image, 'asset' = use existing asset_id, 'adjacent_clip' = chain from neighboring clip (previous for start, next for end)",
        "required": true,
        "enum": [
          "generate",
          "asset",
          "adjacent_clip"
        ]
      },
      {
        "name": "asset_id",
        "type": "string",
        "description": "For 'asset' source: the asset_id to use as keyframe.",
        "required": false
      },
      {
        "name": "prompt",
        "type": "string",
        "description": "For 'generate' source: custom prompt (optional, uses clip's video_prompt if omitted).",
        "required": false
      }
    ]
  },
  {
    "name": "generate_clip_video",
    "category": "Generation",
    "description": "Generate video for a clip using the specified mode.\n\nCalling this on a Generating clip cancels the in-flight run (usage refunded) and restarts with the clip's current state — do that only deliberately, to apply changes; otherwise leave Generating clips alone. Never call this twice for the same clip in one turn.\n\nMODES:\n- \"text-to-video\": Prompt only, no visual input. Best for first clips without characters, or establishing shots.\n- \"image-to-video\": Animate from start keyframe. Optionally set end keyframe too for controlled transitions (replaces first-last-frame). Supports character identity elements via reference_asset_ids.\n- \"reference-to-video\": Character identity preservation + optional style references + optional start frame. THE FLAGSHIP MODE for character-driven clips. Ensures consistent appearance across all clips with same characters. The prompt must frame the action so referenced faces stay visible — the likeness is lost in shots that face away from camera.\n- \"edit-video\": Re-render an existing clip's video with targeted modifications. Preserves camera motion and subject movement, changes only what the prompt specifies (style, colors, wardrobe, environment, objects). Much cheaper than regenerating. Requires clip to have a completed video.\n- \"video-reference\": Generate a new clip guided by another clip's cinematic style — camera movements, pacing, visual tone. Good for establishing a consistent directorial style across scenes.\n- \"extend-video\": Continue an existing clip's video seamlessly from its last frame. Adds ~7s of new footage. The output replaces the clip's video with the combined original + extension. Requires the clip to have a completed video. Use when a clip needs more duration without changing the shot.\n- \"avatar\": Audio-driven talking head via Fabric model. Generates a video of a character speaking with native lip sync. Fabric animates the start keyframe to the TTS audio ONLY — the clip's video prompt is NOT sent to the model, and motion is limited to facial expression and hand gestures (no walking, body travel, or camera moves). Everything visible in the keyframe (background, objects, bystanders) is animated as-is, so verify the keyframe shows exactly what the clip should show. To change an avatar clip's visuals, regenerate its start keyframe image — rewriting the prompt and regenerating has no effect. REQUIRES a start keyframe — call generate_image to create a scene image showing the character in their environment, then set_clip_keyframe(position=\"start\") BEFORE calling generate_clip_video. Without the keyframe, avatar renders on a blank background. Requires the narrative-first skill to be active.\n- \"first-last-frame\": DEPRECATED — use \"image-to-video\" with both start and end keyframes set. Like image-to-video, it cannot run without a start keyframe.\n\nMULTI-SHOT (shots array on clip):\nMulti-shot is NOT a separate mode — it's automatic. When a clip has a shots array (from create_clip), the system sends multi_prompt to the API regardless of which mode you pick. This means multi-shot works WITH any compatible mode:\n- \"text-to-video\" + shots → multi_prompt (prompt-only, no visual input)\n- \"reference-to-video\" + shots → multi_prompt + elements + image_urls (character identity preserved across all shots)\n- \"image-to-video\" + shots → multi_prompt + start image (grounded in a keyframe)\nThe model generates all shots as one continuous video with internal transitions. Max 6 shots, total duration <= 15s.\n\nDURATION: Standard durations are 4, 6, or 8 seconds. Multi-shot individual shots can use 3-15s. Avatar duration is determined by TTS audio length.\n\nWHEN TO USE EACH:\n- Project <= 15s (DEFAULT) → create ONE clip with shots array, use \"reference-to-video\" (if characters) or \"text-to-video\" (no characters). Multi-shot is automatic.\n- First clip with characters / jump cut → \"reference-to-video\" + reference_asset_ids for character identity.\n- Continuous clip with characters (same scene) → prefer \"image-to-video\" from previous frame when character is clearly visible (smoother transitions). Fall back to \"reference-to-video\" if character is absent or frame quality is poor.\n- Clip 2+ continuous, NO characters → \"image-to-video\" with start keyframe from previous clip\n- First clip without characters → \"text-to-video\", or generate a start keyframe + \"image-to-video\"\n- Fix a clip's style/colors/details without re-rolling motion → \"edit-video\"\n- Match another clip's cinematic feel → \"video-reference\" with reference_clip_id\n- Controlled transitions (no characters) → \"image-to-video\" with both start AND end keyframes\n- Talking head / presentation / interview → \"avatar\" (must have start keyframe set first — generate_image + set_clip_keyframe)\n- Extend existing video seamlessly → \"extend-video\" (adds ~7s continuing from last frame)\n\nFILLING DURATION GAPS (voiceover/narration projects):\nWhen a non-avatar clip's narration segment exceeds the video model's max generation length (8s for Veo), the total video across one or more clips MUST equal the narration segment duration. If the user set an explicit clip count, that count is binding — use Extend (and trim) to cover the gap within it; if a hard limitation genuinely conflicts with the count, explain the tradeoff and let the user choose rather than splitting anyway. Otherwise choose the best approach:\n- **Change the shot** (preferred for variety): Create multiple clips with different camera angles or compositions covering the segment. Most cinematic — creates visual rhythm. Use when narration has natural break points.\n- **Image-to-video from last frame**: Generate the first clip, then create a second clip using \"image-to-video\" from the previous clip's last frame. Smooth visual continuation with a subtle cut.\n- **Extend**: Generate the first clip, then call generate_clip_video with mode=\"extend-video\" to add ~7s. Seamless single shot — best for slow reveals, ambient footage, or when cuts would feel jarring.\nAfter generating all video, use edit_timeline with a trim op on the last clip so total video duration exactly matches the narration segment length.\n\nKEYFRAMES: Generated keyframes are cheap and fast. They anchor composition and enable continuity sharing between clips (clip N end keyframe → clip N+1 start keyframe). Worth using whenever you want composition control or clip-to-clip visual bridges.\n\nNATIVE AUDIO: Generations include synchronized audio by default (dialog, ambient sound, effects). Two paths to silence:\n- **At generation**: set audio=false. The audio stream is stripped from the video file after generation, so this works reliably across all models. Use when the user wants their own soundtrack/voiceover and the clip is being generated fresh.\n- **After generation**: use audio_track (op='update' or op='remove') or slice on the clip's audio track (id: clip_audio:<clipId>) to mute, silence end-to-end, lower volume, envelope-duck, or zero specific ranges. Use when clips already exist and the user reports unwanted/hallucinated speech. Do not regenerate to fix audio on existing clips.\n\nDIALOG — when a character speaks ON CAMERA (speech='on_camera'), the clip MUST be lip-synced. Two ways:\n1. **Avatar** (preferred for a consistent/chosen voice): set the clip's dialog to the line, generate a TTS dialogue track, then mode='avatar' — lip-synced to that voice. The scene keyframe is auto-filled from the character reference. The avatar clip's audio is embedded, so slice the floating voiceover track out of its time range.\n2. **Native** (simplest, for complex motion avatar can't do): write the line in the prompt as [Character, tone]: \"The dialog\" with audio=true. The video model speaks it. Don't also lay a competing TTS track over these clips.\n\nNever silence an on-camera speaking clip and carry its words on a separate floating track — the mouth desyncs. Per-clip dialog drives lip sync (avatar audio-matching); off-screen narration comes from generate_audio tracks instead.\n\nNEGATIVE PROMPT: Always set negative_prompt to improve quality. Recommended baseline:\n\"morphing, blurry, disfigured hands, extra fingers, low resolution, cartoonish, watermark\"\nOmit \"text overlay\" from the baseline — it is NOT included by default. Only add \"text overlay\" to negative_prompt when the clip should NOT contain any on-screen text. When the clip's brief or narrative calls for visible text (titles, labels, chyrons, text slams), do NOT include \"text overlay\" in negative_prompt — it actively suppresses the text the user requested.\nAdd scene-specific exclusions as needed (e.g., \"smiling, laughing\" for serious scenes).\n\nCFG_SCALE GUIDANCE:\n- 0.3-0.4: Organic/dreamlike — let the model interpret freely (abstract, artistic shots)\n- 0.5: Default — balanced creativity and prompt adherence\n- 0.6-0.7: Controlled — when specific framing/action matters (dialogue scenes, precise choreography)\n- 0.8+: Strict — for stubborn style changes via edit-video, or when prompt must be followed exactly",
    "params": [
      {
        "name": "clip_id",
        "type": "string",
        "description": "ID of clip to generate video for",
        "required": false
      },
      {
        "name": "mode",
        "type": "string",
        "description": "Generation mode. If the clip has a shots array, multi_prompt is sent automatically — pick the mode based on visual inputs (reference-to-video for characters, image-to-video for start frame, text-to-video for prompt only).",
        "required": false,
        "enum": [
          "text-to-video",
          "image-to-video",
          "reference-to-video",
          "edit-video",
          "video-reference",
          "avatar",
          "extend-video"
        ]
      },
      {
        "name": "reference_asset_ids",
        "type": "array",
        "description": "For reference-to-video: Array of character reference image asset IDs (max 2). Must be real asset IDs.",
        "required": false
      },
      {
        "name": "reference_clip_id",
        "type": "string",
        "description": "For video-reference mode: ID of clip whose video to use as motion/style guide.",
        "required": false
      },
      {
        "name": "cfg_scale",
        "type": "number",
        "description": "Prompt adherence 0-1. Default 0.5. Use 0.3-0.4 for organic/artistic, 0.6-0.7 for precise action/dialogue, 0.8+ for strict style adherence (edit-video corrections).",
        "required": false
      },
      {
        "name": "negative_prompt",
        "type": "string",
        "description": "Always set. Baseline: 'morphing, blurry, disfigured hands, extra fingers, low resolution, cartoonish, watermark'. Only add 'text overlay' when the clip should NOT contain visible text. Omit it for title cards, text slams, or any clip with on-screen text requirements.",
        "required": false
      },
      {
        "name": "use_relaxed_model",
        "type": "boolean",
        "description": "Set to true to use a less restrictive model (full feature parity). Use when the default model's content filter blocks a prompt after rephrasing.",
        "required": false
      },
      {
        "name": "use_most_relaxed_model",
        "type": "boolean",
        "description": "Nuclear option: most permissive model available. Use only after use_relaxed_model also fails. Character consistency may be slightly reduced for reference-to-video.",
        "required": false
      },
      {
        "name": "audio",
        "type": "boolean",
        "description": "Generate with native audio (default: true). Set false ONLY for b-roll/ambient clips (speech='voiceover' or 'none') — silent video that a floating voiceover/music track plays over. Do NOT set false on a clip where a character speaks on camera (speech='on_camera'): silence it and the mouth moves with no synced voice. Lip-sync those via mode='avatar' (the character's chosen voice) or keep audio=true with the line in the prompt. The mere existence of a TTS voiceover track is NOT a reason to silence an on-camera speaking clip. Audio is stripped from the file after generation when false. For existing clips, mute via audio_track op='update' on clip_audio:<clipId> — do not regenerate.",
        "required": false
      },
      {
        "name": "confirm_no_recurring_subjects",
        "type": "boolean",
        "description": "text-to-video on a multi-clip project with no registered characters is blocked, because subjects re-described per prompt drift in appearance across clips. Set true ONLY when no person, creature, or distinct subject appears in more than one clip (unrelated b-roll, abstract or landscape footage). If a subject recurs, register it with update_entities entity_type='character' and generate references instead.",
        "required": false
      }
    ]
  },
  {
    "name": "cancel_video_generation",
    "category": "Generation",
    "description": "Cancel an in-progress video generation for a clip. Use this when the user wants to stop a generating video without restarting it (generate_clip_video on a Generating clip already cancels and restarts in one call). Usage will be refunded.",
    "params": [
      {
        "name": "clip_id",
        "type": "string",
        "description": "ID of clip to cancel video generation for",
        "required": true
      }
    ]
  },
  {
    "name": "get_video_frame",
    "category": "Analysis",
    "description": "Extract a frame from a generated clip video at a specific timestamp. Returns an asset_id for use as a keyframe or reference image. clip_id must be a workflowClips ID (not an asset ID). To analyze an uploaded image or asset, use inspect_frame instead.",
    "params": [
      {
        "name": "clip_id",
        "type": "string",
        "description": "ID of clip with the video",
        "required": true
      },
      {
        "name": "timestamp",
        "type": "number",
        "description": "Timestamp in seconds (use -1 for last frame)",
        "required": false
      }
    ]
  },
  {
    "name": "review_timeline",
    "category": "Other",
    "description": "Structural sanity check of the timeline — clips, audio layers, transitions, effects, overlays, voiceover sync. Fast and cheap (no frame analysis, no vision). Returns a severity-grouped text report.\n\nUse this whenever you want a second pair of eyes on timeline state: after a generation pass, before export, when audio layering gets complex, or when the user reports something feels off. Does not assess visual content of frames or hear audio — use transcribe to read spoken words.\n\n**Usage:**\n- Full review: review_timeline()\n- One clip: review_timeline(clip_id: \"abc\")\n- Zoom in: review_timeline(start_seconds: 4.5, end_seconds: 6.0)\n- Focus: review_timeline(query: \"voiceover gaps and music ducking\")",
    "params": [
      {
        "name": "start_seconds",
        "type": "number",
        "description": "Start of range on global timeline. Default: 0 (beginning)",
        "required": false
      },
      {
        "name": "end_seconds",
        "type": "number",
        "description": "End of range on global timeline. Default: total duration",
        "required": false
      },
      {
        "name": "clip_id",
        "type": "string",
        "description": "Optional: restrict to a specific clip (still uses global timeline coordinates)",
        "required": false
      },
      {
        "name": "query",
        "type": "string",
        "description": "Optional: specific area to focus on (e.g. 'audio ducking', 'transition placement')",
        "required": false
      }
    ]
  },
  {
    "name": "export",
    "category": "Export",
    "description": "Export the project to a final video. Stitches all completed clips on the timeline; clips without generated video are skipped (including image-only clips from add_asset_to_timeline). Before exporting, ensure all clips have generated video — call generate_clip_video on any image-only clips first, or they will appear as black frames or be missing from the export. Export refuses if any clip is stale (its video predates later edits); regenerate those clips first.",
    "params": [
      {
        "name": "allow_stale",
        "type": "boolean",
        "description": "Export even though some clips are stale (their video predates later edits). Only set after the user explicitly confirms they want the outdated footage.",
        "required": false
      }
    ]
  },
  {
    "name": "check_export_status",
    "category": "Export",
    "description": "Check the current export/stitching status of the project's final video. Returns status, videoUrl, duration (seconds), and any error. Use after EXPORT_COMPLETE to verify the output is valid (duration > 0).",
    "params": []
  },
  {
    "name": "acknowledge_notification",
    "category": "Project",
    "description": "Acknowledge a status notification without taking action. Use this when you receive a [Video Status Update] notification and nothing needs doing or saying (e.g., user is still working on something, or all videos are complete). This ends the turn silently — the user sees nothing. Never use it when the user needs to know something (credits exhausted, generation blocked or failing): respond with a message instead.",
    "params": [
      {
        "name": "reason",
        "type": "string",
        "description": "Brief internal note about why no action was taken (not shown to user). Restate only state verified from the timeline context or a tool result this turn — never events you expect but have not received.",
        "required": false
      }
    ]
  },
  {
    "name": "hold_updates",
    "category": "Other",
    "description": "Honor a user request to wait, pause, hold off, or stop. Suppresses your automatic status-update turns ([VIDEO_COMPLETE], [ALL_VIDEOS_COMPLETE], etc.) until the user's next message — in-flight generations keep rendering; you just stop acting and announcing. Call it whenever the user asks you to wait, and confirm to them in one line. Never use it to skip an instruction that names an action.",
    "params": [
      {
        "name": "reason",
        "type": "string",
        "description": "Brief internal note quoting the user's request to wait (not shown to user)",
        "required": false
      }
    ]
  },
  {
    "name": "show_upgrade_card",
    "category": "Billing",
    "description": "Show an inline upgrade card with pricing and checkout. ONLY for users who are free, have no plan, or have a canceled subscription. Do NOT call for users who are already on Pro/Max/Ultra (active or trialing). Don't call unprompted — mention the option first and let the user engage.",
    "params": [
      {
        "name": "credits_needed",
        "type": "number",
        "description": "Internal cost of the blocked operation (do not expose to user)",
        "required": false
      },
      {
        "name": "credits_available",
        "type": "number",
        "description": "Internal available capacity (do not expose to user)",
        "required": false
      },
      {
        "name": "context",
        "type": "string",
        "description": "Brief context about what the user was trying to do (for analytics)",
        "required": false
      }
    ]
  },
  {
    "name": "end_trial_early",
    "category": "Billing",
    "description": "End the user's $1 trial immediately — this charges their card $29 RIGHT NOW (not after the trial period ends). Only works when planStatus is 'trialing'. Before calling, you MUST warn the user that this will charge $29 to their card immediately and get explicit confirmation. NEVER call unprompted.",
    "params": []
  },
  {
    "name": "show_manage_subscription",
    "category": "Billing",
    "description": "Show an inline card that links the user to the Stripe billing portal to fix a payment issue. Use ONLY when the user's subscription is past_due or incomplete. Do NOT use for upgrades or free users.",
    "params": []
  },
  {
    "name": "suggest_support_chat",
    "category": "Other",
    "description": "Surface a card offering the user a live 1:1 support chat with the team. Use when the user is clearly frustrated, blocked by a problem you can't resolve yourself, or asks to reach a person. Offer it in your reply first — don't call it unprompted or for routine creative back-and-forth.",
    "params": [
      {
        "name": "reason",
        "type": "string",
        "description": "Brief internal note on why support was offered (for analytics).",
        "required": false
      }
    ]
  },
  {
    "name": "upgrade_to_max",
    "category": "Billing",
    "description": "Upgrade the user's active Pro subscription to Max ($99/mo, 4x usage capacity). Only works when planStatus is 'active' and plan is Pro. Does NOT work for trialing users — use end_trial_early instead. Call ONLY when the user explicitly agrees to upgrade. NEVER call unprompted.",
    "params": []
  },
  {
    "name": "upgrade_to_ultra",
    "category": "Other",
    "description": "Upgrade the user's active Max subscription to Ultra ($249/mo, highest usage capacity). Only works when planStatus is 'active' and plan is Max. Call ONLY when the user explicitly agrees to upgrade. NEVER call unprompted.",
    "params": []
  },
  {
    "name": "activate_skill",
    "category": "Other",
    "description": "List, activate, or deactivate a skill. Skills extend your capabilities — some add creative direction (cinematic, anime), others unlock new tools and workflows, and others add knowledge (help adds Q&A capability).\n\nActivate the matching skill as soon as the request implies it. When TWO skills plausibly match (e.g. narration over footage vs a demo of a product the user links), activate both in the same turn: each skill's content states its boundaries — decide from the full content, keep the winner, deactivate the other. Routing on content beats guessing from one-line descriptions. Unsure what's available? Use action \"list\" first.\n\nIMPORTANT: Resolve skills BEFORE creating clips or generating audio or video — the active skill changes the entire workflow.",
    "params": [
      {
        "name": "action",
        "type": "string",
        "description": "Action to perform",
        "required": true,
        "enum": [
          "list",
          "activate",
          "deactivate"
        ]
      },
      {
        "name": "skill_name",
        "type": "string",
        "description": "Required for activate/deactivate. The skill name.",
        "required": false
      }
    ]
  },
  {
    "name": "add_asset_to_timeline",
    "category": "Assets",
    "description": "Add a video or image asset to the timeline as a clip. Use the assets tool first to find the asset_id. Videos become playable clips; images become still-frame clips (default 5s). The clip is marked as a jump cut (no continuity with adjacent clips). If the result reports durationSource='default_fallback' for a video, the clip's duration is a placeholder — resolve the real length before reviewing or building on the timeline.",
    "params": [
      {
        "name": "asset_id",
        "type": "string",
        "description": "The ID of the video or image asset to add.",
        "required": true
      },
      {
        "name": "order_index",
        "type": "number",
        "description": "Position in timeline (0-based). Omit to append at the end.",
        "required": false
      },
      {
        "name": "title",
        "type": "string",
        "description": "Optional title for the clip.",
        "required": false
      },
      {
        "name": "duration",
        "type": "number",
        "description": "Override clip duration in seconds. For video assets, defaults to the asset's actual duration. For image assets, defaults to 5s.",
        "required": false
      }
    ]
  },
  {
    "name": "add_asset_from_url",
    "category": "Assets",
    "description": "Convert an image URL into a reusable asset_id.",
    "params": [
      {
        "name": "image_url",
        "type": "string",
        "description": "The image URL to register as an asset.",
        "required": true
      }
    ]
  },
  {
    "name": "generate_audio",
    "category": "Audio",
    "description": "Generate audio using AI models.\n\nMODELS:\n- \"elevenlabs-sfx\": Sound effects (explosions, footsteps, nature sounds). Max 22s.\n- \"elevenlabs-tts\": Single-voice voiceover/narration. Supports voice styles.\n- \"elevenlabs-dialogue\": Multi-voice dialog with word-level timestamps. Pass dialog_inputs array of {text, voice} pairs. Returns voice_segments with per-input timing. Use this for narrative-first projects: generate the audio first, then use the timestamps to create clips with matched durations.\n- \"mmaudio-v2\": Video-synced audio - analyzes video content. Max 30s. Requires video_url.\n- \"minimax-music\": Full song with sung vocals from lyrics (~60s). Pass prompt (music style/genre/mood) + lyrics (with [verse]/[chorus] tags). The ONLY model that can sing — TTS/dialogue models speak, they don't sing.\n\nWHEN TO USE EACH:\n- Sound effects at specific moments → elevenlabs-sfx\n- Single narrator voiceover → elevenlabs-tts\n- Multi-character dialog or narrative-first workflow → elevenlabs-dialogue\n- Realistic audio matching video → mmaudio-v2 (pass video_url)\n- Song with sung lyrics (music videos, theme songs) → minimax-music (pass lyrics)",
    "params": [
      {
        "name": "model",
        "type": "string",
        "description": "Audio generation model to use.",
        "required": false,
        "enum": [
          "elevenlabs-sfx",
          "elevenlabs-tts",
          "elevenlabs-dialogue",
          "mmaudio-v2",
          "minimax-music"
        ]
      },
      {
        "name": "prompt",
        "type": "string",
        "description": "For sfx: describe the sound. For tts: the text to speak. For mmaudio-v2: describe desired audio. For minimax-music: the music style/genre/mood. Not used for elevenlabs-dialogue (use dialog_inputs instead).",
        "required": false
      },
      {
        "name": "lyrics",
        "type": "string",
        "description": "For minimax-music: the song lyrics to sing, with [verse]/[chorus] section tags.",
        "required": false
      },
      {
        "name": "dialog_inputs",
        "type": "array",
        "description": "Exactly the words spoken aloud for this line — TTS reads this verbatim. Never include notes, self-corrections, error text, or other non-speech commentary.",
        "required": false
      },
      {
        "name": "start_time",
        "type": "number",
        "description": "When to start playing (seconds from video start).",
        "required": false
      },
      {
        "name": "duration",
        "type": "number",
        "description": "Duration in seconds. Required for sfx. Limits: sfx=22s, mmaudio-v2=30s.",
        "required": false
      },
      {
        "name": "volume",
        "type": "number",
        "description": "Volume 0-1. Defaults: sfx=0.8, voiceover=1.0.",
        "required": false
      },
      {
        "name": "voice_style",
        "type": "string",
        "description": "For elevenlabs-tts: voice style/mood.",
        "required": false,
        "enum": [
          "narrator",
          "dramatic",
          "warm",
          "professional",
          "energetic",
          "calm"
        ]
      },
      {
        "name": "voice_id",
        "type": "string",
        "description": "For elevenlabs-tts: specific ElevenLabs voice ID.",
        "required": false
      },
      {
        "name": "video_url",
        "type": "string",
        "description": "For mmaudio-v2: URL of video to analyze for audio sync.",
        "required": false
      }
    ]
  },
  {
    "name": "audio_track",
    "category": "Other",
    "description": "Create, edit, remove, or list audio tracks. Set op: 'add' (place new audio), 'update' (change volume/fades/envelope/timing), 'remove' (delete a track or silence clip audio), or 'list' (see all tracks).\n\nop='add' places music/sfx/voiceover on the timeline — use library_track_id (from browse_music_library), asset_id (generated/uploaded audio), or url (chat attachment).\n\nop='update' and op='remove' work on any audio: voiceover/music/sfx tracks (id from audioTracks) OR a clip's native video audio (id format clip_audio:<clipId>). For clip audio, volume=0 or mute=true silences hallucinated speech, ambient noise, or anything baked into a generated clip; volume_envelope ducks under voiceover dynamically. op='remove' on a voiceover/music/sfx track deletes it; on clip_audio it silences the clip end-to-end and clears any envelope (reversible via op='update' mute=false). DESTRUCTIVE: removing a finished voiceover/music/sfx track requires explicit user confirmation unless it is the swap step of a generate-new-then-swap-old replacement. Never bulk-remove tracks during a rebuild flow without an explicit yes from the user.\n\nstart_time/name on op='update' apply to voiceover/music/sfx only, not clip audio.",
    "params": [
      {
        "name": "op",
        "type": "string",
        "description": "add = place new audio. update = change an existing track. remove = delete a track (or silence clip_audio). list = enumerate all tracks.",
        "required": false,
        "enum": [
          "add",
          "update",
          "remove",
          "list"
        ]
      },
      {
        "name": "track_id",
        "type": "string",
        "description": "ID of the track to update or remove. For clip native audio: clip_audio:<clipId>. Required for op='update' and op='remove'.",
        "required": false
      },
      {
        "name": "type",
        "type": "string",
        "description": "Type of audio track. Required for op='add'.",
        "required": false,
        "enum": [
          "music",
          "sfx",
          "voiceover"
        ]
      },
      {
        "name": "name",
        "type": "string",
        "description": "Display name. On op='add' it sets the name; on op='update' it renames (voiceover/music/sfx only).",
        "required": false
      },
      {
        "name": "library_track_id",
        "type": "string",
        "description": "op='add': track ID from browse_music_library results. The URL is resolved internally.",
        "required": false
      },
      {
        "name": "asset_id",
        "type": "string",
        "description": "op='add': asset ID for generated or uploaded audio.",
        "required": false
      },
      {
        "name": "url",
        "type": "string",
        "description": "op='add': direct URL to an audio file (e.g. a user-uploaded chat attachment).",
        "required": false
      },
      {
        "name": "start_time",
        "type": "number",
        "description": "op='add': when to start playing (seconds from video start). op='update': new start time (voiceover/music/sfx only).",
        "required": false
      },
      {
        "name": "duration",
        "type": "number",
        "description": "op='add': duration in seconds. op='update': new audible length (audioTracks only) — e.g. extend music to cover a timeline that grew, or shorten a track. Music loops to fill a duration longer than its source; sfx/voiceover do not loop, so extending past the source leaves silence. Fades stay anchored to the new end.",
        "required": false
      },
      {
        "name": "volume",
        "type": "number",
        "description": "Volume level from 0 to 1. 0 silences the track.",
        "required": false
      },
      {
        "name": "mute",
        "type": "boolean",
        "description": "op='update': mute toggle. Equivalent to volume=0 but reversible to the previous volume.",
        "required": false
      },
      {
        "name": "volume_envelope",
        "type": "array",
        "description": "op='update': keyframed dynamic volume. For voiceover/music/sfx, time is seconds from video start; for clip_audio, time is clip-local seconds (post-trim). Linear interp between points.",
        "required": false
      },
      {
        "name": "fade_in",
        "type": "number",
        "description": "Fade in duration in seconds.",
        "required": false
      },
      {
        "name": "fade_out",
        "type": "number",
        "description": "Fade out duration in seconds.",
        "required": false
      }
    ]
  },
  {
    "name": "browse_music_library",
    "category": "Audio",
    "description": "Browse the royalty-free music library (282 stock tracks across 14 categories).",
    "params": [
      {
        "name": "category",
        "type": "string",
        "description": "Filter by category.",
        "required": false,
        "enum": [
          "chill",
          "classical",
          "comedy",
          "country",
          "electronic",
          "epic-dramatic",
          "fantasy",
          "horror",
          "jazz",
          "rock",
          "romantic-sentimental",
          "underscoring",
          "uplifting",
          "world"
        ]
      },
      {
        "name": "query",
        "type": "string",
        "description": "Search by title keyword.",
        "required": false
      }
    ]
  },
  {
    "name": "analyze_audio_beats",
    "category": "Other",
    "description": "Analyze audio for beats using multi-band spectral analysis. Returns three tiers: downbeats (bar starts — best cut points), strong_hits (high-energy kicks/snares on beat grid), hits (all detected onsets with score 0-1). Also returns bpm and beat_grid (tempo positions). Pass track_id to auto-store results for timeline visualization. WORKFLOW: 1) Pick downbeats or strong_hits as clip boundaries. 2) Create each clip with duration=5. 3) Use edit_timeline with a trim op on EVERY clip to fit beat intervals exactly.",
    "params": [
      {
        "name": "url",
        "type": "string",
        "description": "URL of the audio file to analyze.",
        "required": false
      },
      {
        "name": "track_id",
        "type": "string",
        "description": "Optional: audio track ID from the project. If provided, the URL is resolved automatically.",
        "required": false
      }
    ]
  },
  {
    "name": "link_clips_to_audio",
    "category": "Other",
    "description": "Bind clips to an audio narration segment. Trimming can only SHORTEN an over-long group to match the audio; it cannot create coverage. When the result reports a coverage gap (\"not enough video\"), the fix is to ADD video — extend clip durations or insert clips — never to ignore it. Transitions are accounted for. Use this after creating clips for a voiceover/narration segment.",
    "params": [
      {
        "name": "clip_ids",
        "type": "array",
        "description": "Ordered list of clip IDs to link.",
        "required": true
      },
      {
        "name": "audio_track_id",
        "type": "string",
        "description": "ID of the audio track.",
        "required": true
      },
      {
        "name": "voice_segment_index",
        "type": "number",
        "description": "Index into voiceSegments array.",
        "required": true
      },
      {
        "name": "name",
        "type": "string",
        "description": "Optional label for this link group.",
        "required": false
      }
    ]
  },
  {
    "name": "unlink_clips",
    "category": "Other",
    "description": "Remove a link group. Clips keep their current trims but are no longer auto-synced.",
    "params": [
      {
        "name": "link_group_id",
        "type": "string",
        "description": "ID of the link group to remove.",
        "required": true
      }
    ]
  },
  {
    "name": "slice",
    "category": "Other",
    "description": "Cut a time range out of any timeline item. Auto-detects type from id: audio track, video clip, text overlay, or clip native audio (id format: clip_audio:<clipId>). Voice/music/sfx + clips + overlays produce 'before' and 'after' pieces; the cut range is removed. Clip audio slicing silences the cut range via volume envelope — visuals untouched, no new clips. For slicing a voiceover track around an avatar clip, pass clip_id instead of cut_start/cut_end.",
    "params": [
      {
        "name": "id",
        "type": "string",
        "description": "ID of the timeline item to slice.",
        "required": true
      },
      {
        "name": "cut_start",
        "type": "number",
        "description": "Timeline position (seconds) where the cut begins. Not needed if clip_id is provided.",
        "required": false
      },
      {
        "name": "cut_end",
        "type": "number",
        "description": "Timeline position (seconds) where the cut ends. Not needed if clip_id is provided.",
        "required": false
      },
      {
        "name": "clip_id",
        "type": "string",
        "description": "ID of a clip whose timeline position should be used as the cut range. The system computes the clip's exact start/end on the timeline. Use this instead of cut_start/cut_end when slicing a voiceover track around an avatar clip.",
        "required": false
      }
    ]
  },
  {
    "name": "set_clip_effects",
    "category": "Post-Production",
    "description": "Apply visual effects to a clip. Only provided fields are updated. Omit fields to leave them unchanged.",
    "params": [
      {
        "name": "clip_id",
        "type": "string",
        "description": "ID of the clip.",
        "required": false
      },
      {
        "name": "fade_in",
        "type": "object",
        "description": "Fade from color at clip start. Omit to leave unchanged.",
        "required": false,
        "enum": [
          "black",
          "white"
        ]
      },
      {
        "name": "fade_out",
        "type": "object",
        "description": "Fade to color at clip end. Omit to leave unchanged.",
        "required": false,
        "enum": [
          "black",
          "white"
        ]
      },
      {
        "name": "color_grade",
        "type": "string",
        "description": "Color grading preset. 'none' to remove.",
        "required": false,
        "enum": [
          "none",
          "cinematic_warm",
          "cinematic_cool",
          "desaturated",
          "high_contrast",
          "vintage",
          "noir",
          "sepia",
          "bw",
          "bleach_bypass",
          "cross_process",
          "golden_hour",
          "moonlight",
          "teal_orange",
          "film_fade"
        ]
      },
      {
        "name": "color_grade_intensity",
        "type": "number",
        "description": "Color grade intensity 0-1 (default 1.0 = full strength). Use lower values for subtler looks.",
        "required": false
      },
      {
        "name": "letterbox",
        "type": "boolean",
        "description": "Add 2.39:1 cinematic letterbox bars.",
        "required": false
      },
      {
        "name": "vignette",
        "type": "number",
        "description": "Vignette intensity 0-1 (0 to remove).",
        "required": false
      },
      {
        "name": "blur",
        "type": "number",
        "description": "Blur intensity 0-20 pixels (0 to remove). Use for dream sequences or depth-of-field.",
        "required": false
      },
      {
        "name": "bloom",
        "type": "number",
        "description": "Bloom/glow intensity 0-1 (0 to remove). Adds brightness and soft glow.",
        "required": false
      },
      {
        "name": "chromatic_aberration",
        "type": "number",
        "description": "Chromatic aberration 0-1 (0 to remove). RGB color fringing for a lo-fi or sci-fi look.",
        "required": false
      },
      {
        "name": "vhs",
        "type": "boolean",
        "description": "VHS retro effect: scanlines, tracking glitches.",
        "required": false
      },
      {
        "name": "shake",
        "type": "number",
        "description": "Camera shake intensity 0-1 (0 to remove). Simulates handheld or earthquake.",
        "required": false
      },
      {
        "name": "motion_preset",
        "type": "string",
        "description": "Named motion preset. Use instead of raw ken_burns values for convenience.",
        "required": false,
        "enum": [
          "slow_zoom_in",
          "zoom_in",
          "dramatic_zoom_in",
          "slow_zoom_out",
          "zoom_out",
          "pan_left",
          "pan_right",
          "pan_up",
          "pan_down",
          "ken_burns_classic",
          "dramatic_push",
          "reveal_pan",
          "float"
        ]
      },
      {
        "name": "ken_burns",
        "type": "object",
        "description": "Starting zoom level (1.0 = normal, 1.2 = slightly zoomed in).",
        "required": false
      }
    ]
  },
  {
    "name": "text_overlay",
    "category": "Other",
    "description": "Create, edit, or remove animated text overlays. Set op: 'add' (new overlay), 'update' (change an existing one — only provided fields change), or 'remove' (delete one).\n\nAnchor overlays to a clip (preferred) or to a global timeline position. Anchored overlays stay glued to their clip through reorders, trims, duration changes, and transitions — pass clip_id + clip_offset instead of computing absolute seconds. For a project or scene title, default to cinematic_title (a thick contemporary sans with personality); reach for elegant_title or standard_title for a different tone, and the high-impact titles (hero_title, neon_title, youtube_bold, action_title) only for big or high-energy moments. Place short punchy titles centered, but anchor longer titles to the left (lower_left, upper_left, or left_center) — a centered block reads awkwardly once it wraps — and add an eyebrow kicker for an editorial feel. Use lower-third presets for names, roles, and captions. The preset supplies font, color, shadow, animation, and decorations automatically; explicit style fields override it. Available presets: cinematic_title, elegant_title, standard_title, chapter_title, hero_title, neon_title, youtube_bold, film_noir, vintage_poster, action_title, clean_lower_third, banner_lower_third, accent_lower_third, minimal_lower_third, gradient_lower_third, documentary_lower_third, top_badge, corner_label, bottom_tag, left_callout, center_quote, right_pullout, breaking, statement, simple, typewriter_text, light_sans.",
    "params": [
      {
        "name": "op",
        "type": "string",
        "description": "add = create a new overlay (needs text + duration). update = change an existing overlay (needs overlay_id; only provided fields change). remove = delete an overlay (needs overlay_id).",
        "required": false,
        "enum": [
          "add",
          "update",
          "remove"
        ]
      },
      {
        "name": "overlay_id",
        "type": "string",
        "description": "ID of the overlay to update or remove. Required for op='update' and op='remove'.",
        "required": false
      },
      {
        "name": "text",
        "type": "string",
        "description": "Text content. Include actual newline characters for multi-line text. Required for op='add'.",
        "required": false
      },
      {
        "name": "clip_id",
        "type": "string",
        "description": "Anchor the overlay to this clip (preferred over start_time). The overlay starts at the clip's start plus clip_offset and is automatically re-timed whenever the timeline changes.",
        "required": false
      },
      {
        "name": "clip_offset",
        "type": "number",
        "description": "Seconds into the anchored clip where the overlay starts (default 0). Only used with clip_id.",
        "required": false
      },
      {
        "name": "start_time",
        "type": "number",
        "description": "Start time in seconds on the global timeline. Alternative to clip_id — the overlay is auto-anchored to whichever clip contains this time. Compute from the realized timeline (the Timeline header total), not the brief's target duration or music length — clips often total less than the target.",
        "required": false
      },
      {
        "name": "duration",
        "type": "number",
        "description": "Duration in seconds. Required for op='add'.",
        "required": false
      },
      {
        "name": "preset",
        "type": "string",
        "description": "Preset ID. Applies all style settings automatically. Can be overridden by explicit style fields.",
        "required": false,
        "enum": [
          "cinematic_title",
          "elegant_title",
          "standard_title",
          "chapter_title",
          "hero_title",
          "neon_title",
          "youtube_bold",
          "film_noir",
          "vintage_poster",
          "action_title",
          "clean_lower_third",
          "banner_lower_third",
          "accent_lower_third",
          "minimal_lower_third",
          "gradient_lower_third",
          "documentary_lower_third",
          "top_badge",
          "corner_label",
          "bottom_tag",
          "left_callout",
          "center_quote",
          "right_pullout",
          "breaking",
          "statement",
          "simple",
          "typewriter_text",
          "light_sans"
        ]
      },
      {
        "name": "position",
        "type": "string",
        "description": "Where the text sits. center suits short punchy titles; for longer titles anchor left — lower_left, upper_left, or left_center read far better than a centered block. lower_third/upper_third are for captions and names.",
        "required": false,
        "enum": [
          "center",
          "lower_left",
          "upper_left",
          "left_center",
          "bottom_center",
          "top_center",
          "lower_third",
          "upper_third"
        ]
      },
      {
        "name": "style",
        "type": "string",
        "description": "Legacy visual style (prefer preset instead).",
        "required": false,
        "enum": [
          "title",
          "subtitle",
          "lower_third",
          "caption",
          "credits"
        ]
      },
      {
        "name": "animation",
        "type": "string",
        "description": "Shorthand — sets both enter and exit animation. Use enter_animation/exit_animation for independent control.",
        "required": false,
        "enum": [
          "fade",
          "slide_up",
          "slide_down",
          "slide_left",
          "slide_right",
          "scale",
          "typewriter",
          "glitch",
          "bounce",
          "blur_in",
          "zoom_in",
          "rotate_in",
          "wave",
          "letter_by_letter",
          "none"
        ]
      },
      {
        "name": "enter_animation",
        "type": "string",
        "description": "Entrance animation (overrides animation for the enter phase).",
        "required": false,
        "enum": [
          "fade",
          "slide_up",
          "slide_down",
          "slide_left",
          "slide_right",
          "scale",
          "typewriter",
          "glitch",
          "bounce",
          "blur_in",
          "zoom_in",
          "rotate_in",
          "wave",
          "letter_by_letter",
          "none"
        ]
      },
      {
        "name": "exit_animation",
        "type": "string",
        "description": "Exit animation (overrides animation for the exit phase).",
        "required": false,
        "enum": [
          "fade",
          "slide_up",
          "slide_down",
          "slide_left",
          "slide_right",
          "scale",
          "blur_out",
          "zoom_out",
          "rotate_out",
          "glitch",
          "none"
        ]
      },
      {
        "name": "emphasis_animation",
        "type": "string",
        "description": "Continuous animation during the hold phase (between enter and exit). Subtle looping effect.",
        "required": false,
        "enum": [
          "pulse",
          "breathe",
          "float",
          "glow_pulse",
          "color_shift",
          "none"
        ]
      },
      {
        "name": "animation_duration",
        "type": "number",
        "description": "Animation duration in seconds (default: 0.5). Applies to both enter and exit unless overridden.",
        "required": false
      },
      {
        "name": "enter_duration",
        "type": "number",
        "description": "Enter animation duration in seconds (overrides animation_duration for enter phase).",
        "required": false
      },
      {
        "name": "exit_duration",
        "type": "number",
        "description": "Exit animation duration in seconds (overrides animation_duration for exit phase).",
        "required": false
      },
      {
        "name": "color",
        "type": "string",
        "description": "Text color as hex (default: #FFFFFF).",
        "required": false
      },
      {
        "name": "background",
        "type": "string",
        "description": "Background color as hex with alpha (default: style-dependent).",
        "required": false
      },
      {
        "name": "subtitle_text",
        "type": "string",
        "description": "Subtitle line for lower third presets (op='add').",
        "required": false
      },
      {
        "name": "eyebrow",
        "type": "string",
        "description": "Optional small kicker/label shown above the title (e.g. a brand or section name). Pairs well with a left-anchored title for a designed, editorial look. On op='update', pass an empty string to remove it.",
        "required": false
      },
      {
        "name": "font_family",
        "type": "string",
        "description": "Override font family (e.g. 'Inter', 'Bebas Neue', 'Playfair Display').",
        "required": false
      },
      {
        "name": "font_size",
        "type": "number",
        "description": "Override font size in pixels.",
        "required": false
      },
      {
        "name": "font_weight",
        "type": "number",
        "description": "Override font weight (100-900).",
        "required": false
      },
      {
        "name": "custom_css",
        "type": "string",
        "description": "Raw CSS properties applied on top of all other styles. Semicolon-separated static declarations, e.g. 'background: linear-gradient(90deg, #ff0, #f00); -webkit-background-clip: text; -webkit-text-fill-color: transparent'. Useful for gradient text, backdrop-filter, clip-path, custom text-shadow, or any static CSS effect beyond the standard fields. @keyframes/animation/transition cannot render here — use the animation fields for motion.",
        "required": false
      }
    ]
  },
  {
    "name": "generate_captions",
    "category": "Other",
    "description": "Generate word-by-word captions from clip transcriptions. Creates text overlays with word-level timing so each word animates as it's spoken. Requires clips to have completed transcriptions (transcriptionStatus=\"ready\"). Removes any existing caption overlays before generating new ones. Omit style for the polished default ('bold').\n\nAvailable presets:\n- bold (default): Clean heavy sans on a soft scrim, gentle word pop\n- youtube: Thick black outline, punchy yellow highlight per word\n- tiktok_pop: Towering display font, springy bounce per word\n- hormozi: Active word sits in a bright highlight box\n- spotlight: Active word bright, others dimmed\n- colorful: Soft words, vivid violet accent on active\n- cascade: Words stacked vertically, active lifts\n- neon: Soft glow that intensifies on the active word\n- outline: Stroke-only text, fills on active word\n- solo: One word at a time, clean and centered\n- word_pop: One word at a time, snappy color pop\n- word_slam: One word at a time, towering outlined slam (TikTok)\n- minimal: Small, clean, understated\n- cinema: Warm film-subtitle bar (no word emphasis)\n- elegant: Serif subtitles on a dark bar\n- typewriter: Monospace, words appear as spoken\n- broadcast: Condensed news-style captions",
    "params": [
      {
        "name": "style",
        "type": "string",
        "description": "Caption style preset name. Each preset defines font, colors, animation, and effects.",
        "required": false,
        "enum": [
          "bold",
          "youtube",
          "tiktok_pop",
          "hormozi",
          "spotlight",
          "colorful",
          "cascade",
          "neon",
          "outline",
          "solo",
          "word_pop",
          "word_slam",
          "minimal",
          "cinema",
          "elegant",
          "typewriter",
          "broadcast"
        ]
      },
      {
        "name": "position",
        "type": "string",
        "description": "Where captions sit on screen. Defaults to the style's own placement.",
        "required": false,
        "enum": [
          "bottom_center",
          "center",
          "top_center"
        ]
      }
    ]
  },
  {
    "name": "generate_long_avatar",
    "category": "Generation",
    "description": "Generate a long-form avatar video (over 60s). Splits TTS audio into ~55s chunks, generates avatar videos in parallel, then stitches them together with crossfade. Use this for presentations, monologues, interviews, or any talking-head content exceeding 60 seconds. Requires: character with voice + dialog + reference image + TTS audio ready.",
    "params": [
      {
        "name": "clip_id",
        "type": "string",
        "description": "ID of the clip to generate long-form avatar for",
        "required": true
      }
    ]
  },
  {
    "name": "generate_image",
    "category": "Generation",
    "description": "Generate an image from a text prompt, or transform existing image(s) with a prompt (image-to-image).\n\nFor image-to-image: pass input_image (an asset_id) of an existing asset to transform it with the prompt. Image-to-image is a full generative repaint, NOT a precise edit: the entire frame is redrawn, so text, logos, credits, and fine layout from the source will shift or corrupt. When the user wants their image used as-is — or has told you to stop altering it — never route it through this tool; use the asset directly (e.g. set_clip_keyframe with source=\"asset\").\nFor multi-image composition: pass input_images (2-4 asset_ids) — e.g. place a cast character INTO a scouted location (input_images=[character_ref, location_ref], prompt describes the staging), or combine a character with a product shot.\nReturns an asset_id that can be used with generate_clip_video or set as clip keyframe, plus the image_url to show the image to the user (e.g. as ask_questions option image_url) — always use this returned URL, never construct one.",
    "params": [
      {
        "name": "prompt",
        "type": "string",
        "description": "Detailed description of the image to generate. For img2img, describe the desired result.",
        "required": true
      },
      {
        "name": "input_image",
        "type": "string",
        "description": "Optional: asset_id of a source image to transform (full generative repaint guided by the prompt — not a faithful edit; do not use when the source must stay unchanged).",
        "required": false
      },
      {
        "name": "input_images",
        "type": "array",
        "description": "Optional: 2-4 asset_ids to combine into one image (multi-image edit). Use for character-in-setting composites and product staging.",
        "required": false
      },
      {
        "name": "aspect_ratio",
        "type": "string",
        "description": "Aspect ratio of the image. Default: 16:9",
        "required": false,
        "enum": [
          "16:9",
          "9:16",
          "1:1",
          "4:3",
          "3:4"
        ]
      },
      {
        "name": "negative_prompt",
        "type": "string",
        "description": "What to avoid in the image (e.g., 'blurry, low quality, text, watermark')",
        "required": false
      },
      {
        "name": "clip_id",
        "type": "string",
        "description": "Optional: ID of clip to attach this image to as a keyframe",
        "required": false
      },
      {
        "name": "position",
        "type": "string",
        "description": "If clip_id provided, which keyframe position to set: start or end",
        "required": false,
        "enum": [
          "start",
          "end"
        ]
      }
    ]
  },
  {
    "name": "inspect_frame",
    "category": "Analysis",
    "description": "Analyze an image/frame to make continuity and reference decisions. Only works on image assets — NOT video files. For uploaded videos, use review_timeline or extract_frame instead.\n\nUse this tool BEFORE generating a new clip to decide:\n- Whether to use this frame as a starting reference\n- If the target character is visible and suitable\n- If the composition/angle works for the next shot\n- Whether to use character reference images instead\n\nWHEN TO USE:\n- Before clip 2+ generation to check if previous clip's last frame is suitable\n- When deciding between using previous frame vs character reference images\n- After extracting a frame to evaluate quality/suitability\n\nRETURNS:\n- Answers to your specific questions\n- Recommendation: \"use_this_frame\" | \"use_character_reference\" | \"generate_keyframe\"\n- Detected elements (characters, objects, setting)\n- Quality assessment",
    "params": [
      {
        "name": "asset_id",
        "type": "string",
        "description": "Asset ID of the image/frame to analyze",
        "required": true
      },
      {
        "name": "questions",
        "type": "array",
        "description": "Specific questions to answer about the frame (e.g., 'Is Maya visible?', 'Is this suitable for a close-up of Jake?')",
        "required": true
      },
      {
        "name": "next_clip_characters",
        "type": "array",
        "description": "Character names that should be in the next clip",
        "required": false
      },
      {
        "name": "next_clip_shot_type",
        "type": "string",
        "description": "The shot type for the next clip (e.g., 'close-up', 'wide shot')",
        "required": false
      },
      {
        "name": "is_jump_cut",
        "type": "boolean",
        "description": "TRUE if next clip is in different location or time (previous frame shouldn't be used as starting point)",
        "required": false
      }
    ]
  },
  {
    "name": "set_ui_layout",
    "category": "Other",
    "description": "Control the project editor's panel layout. Use this to adapt the UI for the current workflow phase — expand the script panel for writing-focused work, emphasize the timeline for editing, or switch sidebar tabs to relevant tools. Call this proactively when the workflow shifts focus (e.g., scripting to editing, planning to generation).",
    "params": [
      {
        "name": "preset",
        "type": "string",
        "description": "Layout preset. 'script-focus': expands story panel with script editor, minimizes clips. 'audio-focus': emphasizes audio timeline and transcript. 'timeline-focus': maximizes clips panel and timeline. 'default': balanced layout.",
        "required": true,
        "enum": [
          "default",
          "script-focus",
          "audio-focus",
          "timeline-focus"
        ]
      },
      {
        "name": "active_sidebar_tab",
        "type": "string",
        "description": "Which right sidebar tab to activate. Optional — preset sets a sensible default.",
        "required": false,
        "enum": [
          "script",
          "media",
          "music",
          "effects",
          "text",
          "transitions",
          "captions",
          "activity"
        ]
      },
      {
        "name": "emphasis",
        "type": "string",
        "description": "Free-form hint about what the user should focus on. Shown as a subtle label in the UI.",
        "required": false
      }
    ]
  },
  {
    "name": "ask_questions",
    "category": "Other",
    "description": "Present one or more questions with options for the user to choose from — answered one at a time, then all answers return in a single batch. Renders an interactive picker per question, far better than listing options as text. MUST be used for voice selection, music selection, aspect ratio, and any decision with 2-10 concrete options. Pass a single-item questions array for one question; pass several to gather a vague brief at once (subject, tone, format, audience). 1-6 questions.\n\nMark exactly ONE option per question recommended=true — your honest pick. The picker shows a \"Decide for me\" action that selects it; an answer of \"Decide for me\" means your recommended option for that question, proceed with it. If the answers include \"Stop asking — make the remaining decisions yourself\", the user is done being consulted: set autonomy=\"surprise_me\" via update_brief, resolve every unanswered question with your recommendation, and proceed without further gates. A reply that matches no option is free text, not a pick — treat it as the user's own answer or new direction, never silently substitute the closest option.\n\nBeyond image_url, options can be visual without any image: preview_url (audio play button), swatches (color palette), aspect (format frame diagram), caption_preset (live-rendered caption sample). Use these — visual options beat text labels.",
    "params": [
      {
        "name": "questions",
        "type": "array",
        "description": "2-6 questions to present in sequence",
        "required": false,
        "enum": [
          "format",
          "story",
          "cast",
          "world",
          "product",
          "look",
          "sound",
          "type",
          "process"
        ]
      }
    ]
  },
  {
    "name": "history",
    "category": "Other",
    "description": "View, undo, or redo your past actions on this project. Use this when you realize you made a mistake, or the user asks to go back, undo, revert, or redo something. All your state-changing tool calls (creating/deleting/editing clips, changing settings, audio tracks, text overlays, etc.) are recorded and reversible.",
    "params": [
      {
        "name": "action",
        "type": "string",
        "description": "list: show recent actions with IDs. undo: revert the last action (or a specific one by action_id). If omitted, targets the most recent. redo: re-apply the last undone action.",
        "required": true,
        "enum": [
          "list",
          "undo",
          "redo"
        ]
      },
      {
        "name": "action_id",
        "type": "string",
        "description": "Target a specific action by ID (from list). Omit to target the most recent.",
        "required": false
      },
      {
        "name": "limit",
        "type": "number",
        "description": "For list: max actions to show (default 10).",
        "required": false
      }
    ]
  },
  {
    "name": "list_user_projects",
    "category": "Other",
    "description": "List the user's other projects with one-line summaries. Use this when the user references prior work, asks about what they've made, or when you want context about their creative history.",
    "params": [
      {
        "name": "limit",
        "type": "number",
        "description": "Max projects to return (default 20)",
        "required": false
      }
    ]
  },
  {
    "name": "read_project_summary",
    "category": "Other",
    "description": "Read the full summary of a specific project by its shortcode. Use after list_user_projects to get detailed context about a particular project.",
    "params": [
      {
        "name": "shortcode",
        "type": "string",
        "description": "Project shortcode from list_user_projects",
        "required": true
      }
    ]
  },
  {
    "name": "read_project_conversation",
    "category": "Other",
    "description": "Read paginated conversation messages from another project. Use to understand the full creative process and decisions made.",
    "params": [
      {
        "name": "shortcode",
        "type": "string",
        "description": "Project shortcode",
        "required": true
      },
      {
        "name": "offset",
        "type": "number",
        "description": "Message offset (default 0)",
        "required": false
      },
      {
        "name": "limit",
        "type": "number",
        "description": "Messages per page (default 20)",
        "required": false
      }
    ]
  },
  {
    "name": "capture_website",
    "category": "Other",
    "description": "Capture a website for a product-demo video: crawls the site's top nav, screenshots each page (viewport + full page), and extracts copy, headings, and clickable elements. Returns a page list with shot URLs, per-page visual summaries, and a suggested brand accent. Takes ~1-3 minutes — tell the user it's running before calling. After it returns, present the captured pages (show_media_grid if you have it: type 'image', path+thumbnail_url = the viewportShot URLs), then use get_page_details on the pages you'll feature and set_demo_scenes to build the video.",
    "params": [
      {
        "name": "url",
        "type": "string",
        "description": "Website URL, e.g. https://example.com",
        "required": true
      },
      {
        "name": "max_pages",
        "type": "number",
        "description": "Max pages to capture (default 7, max 10)",
        "required": false
      },
      {
        "name": "dark",
        "type": "boolean",
        "description": "Capture with prefers-color-scheme: dark",
        "required": false
      }
    ]
  },
  {
    "name": "get_page_details",
    "category": "Other",
    "description": "Get choreography data for captured pages: heading positions and clickable-element rects in page coordinates (CSS px, 1920-wide; y is document offset). Use these to aim set_demo_scenes camera keyframes at real sections and cursor steps at real buttons. Only request the pages you're featuring.",
    "params": [
      {
        "name": "slugs",
        "type": "array",
        "description": "Page slugs from capture_website",
        "required": true
      }
    ]
  },
  {
    "name": "set_demo_scenes",
    "category": "Other",
    "description": "Set the product-demo storyboard: replaces the project's demo scenes with this list, one timeline clip per scene, in order. Scenes render synthetically (no video generation): screenshots inside a browser window with smooth camera moves and a synthetic cursor, plus title/tilt/outro beats. Returns each scene's timeline startTime — then narrate with generate_audio (model elevenlabs-tts) at those start_times. Coordinates are page CSS px from get_page_details. Camera keyframes center point (x,y) at the given zoom (1 = full page width fits the frame; at zoom Z the visible window is ~1920/Z x 980/Z). 2-3 camera moves per scene max, zooms 1.0-2.2, end settled on what narration mentions.",
    "params": [
      {
        "name": "meta",
        "type": "object",
        "description": "Demo styling, from the capture's suggested accent/theme unless the user overrides",
        "required": false,
        "enum": [
          "dark",
          "light"
        ]
      },
      {
        "name": "scenes",
        "type": "array",
        "description": "1-12 scenes. Arc: title hook → tilt reveal → 2-4 browse feature beats → outro CTA.",
        "required": false,
        "enum": [
          "title",
          "tilt",
          "browse",
          "outro"
        ]
      }
    ]
  },
  {
    "name": "import_youtube",
    "category": "Other",
    "description": "Import audio or video from a YouTube URL into the user's library and add it to the current project. Downloads via yt-dlp, extracts the requested segment, and uploads to CDN. Use when the user shares a YouTube link or asks to use content from YouTube.",
    "params": []
  },
  {
    "name": "assets",
    "category": "Other",
    "description": "Find and inspect the user's assets. Three modes by argument:\n• Pass asset_id → full details for that asset (generation metadata, dimensions, duration, labels, tags, description, analysis, source project).\n• Pass query (and optionally scope/folder) → keyword search. Matches name, folder, labels, tags, description, generation prompt, model, and source project title. Default scope is the current project; scope=\"library\" searches user-level uploads/imports/YouTube/Drive/Dropbox not bound to any project; scope=\"all_projects\" searches every project the user owns.\n• Pass neither query nor scope/folder → browse all assets in the current project, including pending and failed imports (each carries a status and, when present, an error).",
    "params": [
      {
        "name": "asset_id",
        "type": "string",
        "description": "Inspect a single asset by ID — returns full details.",
        "required": false
      },
      {
        "name": "query",
        "type": "string",
        "description": "Keyword search across the chosen scope.",
        "required": false
      },
      {
        "name": "scope",
        "type": "string",
        "description": "Search scope. Default: \"project\". (Ignored when browsing the current project.)",
        "required": false,
        "enum": [
          "project",
          "library",
          "all_projects"
        ]
      },
      {
        "name": "type",
        "type": "string",
        "description": "Filter by asset type.",
        "required": false,
        "enum": [
          "image",
          "video",
          "audio"
        ]
      },
      {
        "name": "folder",
        "type": "string",
        "description": "Filter by exact folder name (library scope).",
        "required": false
      },
      {
        "name": "offset",
        "type": "number",
        "description": "Skip first N assets. Default 0.",
        "required": false
      },
      {
        "name": "limit",
        "type": "number",
        "description": "Max assets to return. Default 20, max 50.",
        "required": false
      }
    ]
  },
  {
    "name": "transcribe",
    "category": "Other",
    "description": "Transcribe speech to text from audio. This is the only tool that hears audio — review_timeline and frame tools are vision/metadata only. Pass exactly one source: asset_id (an audio/video asset from assets), clip_id (a timeline clip's video/TTS audio), track_id (a timeline audio track), or path (a local file on the user's computer; desktop app only). Returns the transcript text with language and word timing; asset/clip/track results are cached after the first run.",
    "params": [
      {
        "name": "asset_id",
        "type": "string",
        "description": "Audio or video asset ID from the assets tool.",
        "required": false
      },
      {
        "name": "clip_id",
        "type": "string",
        "description": "Timeline clip ID — transcribes the clip's video/TTS audio.",
        "required": false
      },
      {
        "name": "track_id",
        "type": "string",
        "description": "Audio track ID from the timeline (voiceover, music, sfx).",
        "required": false
      },
      {
        "name": "path",
        "type": "string",
        "description": "Path to a local video or audio file (supports ~). Desktop app only.",
        "required": false
      }
    ]
  },
  {
    "name": "import_asset",
    "category": "Other",
    "description": "Import an asset into the current project. Works for any source: library assets (workflowId-less) or assets from another project. Creates a new asset referencing the same URL in the current project so it can be used with set_clip_keyframe, add_asset_to_timeline, generate_clip_video, etc. Preserves the source asset in its original location.",
    "params": [
      {
        "name": "asset_id",
        "type": "string",
        "description": "The asset_id to import.",
        "required": true
      }
    ]
  },
  {
    "name": "scan_local_directory",
    "category": "Desktop",
    "description": "Scan a local directory on the user's computer for media files. Only available in the desktop app. Results are processed by the desktop client and sent back.",
    "params": [
      {
        "name": "directory",
        "type": "string",
        "description": "Directory path to scan (e.g. ~/Movies)",
        "required": true
      }
    ]
  },
  {
    "name": "get_local_file_metadata",
    "category": "Desktop",
    "description": "Get technical metadata (duration, resolution, codec) for a local file via ffprobe. Desktop app only.",
    "params": [
      {
        "name": "file_path",
        "type": "string",
        "description": "Path to the local file",
        "required": true
      }
    ]
  },
  {
    "name": "organize_local_media",
    "category": "Desktop",
    "description": "Scan a directory, analyze media files, and register them in the user's media library with labels. Desktop app only.",
    "params": [
      {
        "name": "directory",
        "type": "string",
        "description": "Directory path to scan",
        "required": true
      },
      {
        "name": "folder_label",
        "type": "string",
        "description": "Label for this folder (e.g. 'Vacation 2024')",
        "required": false
      }
    ]
  },
  {
    "name": "read_file",
    "category": "Other",
    "description": "Read a local file. For text files (scripts, subtitles, notes, config): returns the text content. For media files (video, audio, image): runs ffprobe and returns technical metadata (duration, resolution, codec, bitrate). Use offset/limit for large text files.",
    "params": [
      {
        "name": "path",
        "type": "string",
        "description": "File path (supports ~ for home directory)",
        "required": true
      },
      {
        "name": "offset",
        "type": "number",
        "description": "Line number to start reading from (0-based, for text files)",
        "required": false
      },
      {
        "name": "limit",
        "type": "number",
        "description": "Maximum number of lines to read (for text files)",
        "required": false
      }
    ]
  },
  {
    "name": "write_file",
    "category": "Other",
    "description": "Write content to a local file. Creates parent directories automatically. Use for saving storyboards, shot lists, scene breakdowns, subtitles, notes, or any text output.",
    "params": [
      {
        "name": "path",
        "type": "string",
        "description": "File path (supports ~ for home directory)",
        "required": true
      },
      {
        "name": "content",
        "type": "string",
        "description": "Content to write",
        "required": true
      },
      {
        "name": "append",
        "type": "boolean",
        "description": "Append to existing file instead of overwriting (default: false)",
        "required": false
      }
    ]
  },
  {
    "name": "list_directory",
    "category": "Other",
    "description": "List contents of a local directory. Use before scan_media to orient yourself, or to find specific files by pattern.",
    "params": [
      {
        "name": "path",
        "type": "string",
        "description": "Directory path (supports ~ for home directory)",
        "required": true
      },
      {
        "name": "recursive",
        "type": "boolean",
        "description": "List subdirectories recursively (default: false)",
        "required": false
      },
      {
        "name": "pattern",
        "type": "string",
        "description": "Glob pattern to filter files (e.g. '*.mp4', '*.{jpg,png}')",
        "required": false
      },
      {
        "name": "max_items",
        "type": "number",
        "description": "Maximum items to return (default: 200)",
        "required": false
      }
    ]
  },
  {
    "name": "run_command",
    "category": "Other",
    "description": "Run a shell command locally. Safety-guarded: only allows ffmpeg, ffprobe, mediainfo, exiftool, and other media-related CLI tools. Use for custom media processing that other tools don't cover.",
    "params": [
      {
        "name": "command",
        "type": "string",
        "description": "Shell command to execute",
        "required": true
      },
      {
        "name": "cwd",
        "type": "string",
        "description": "Working directory (supports ~, default: home)",
        "required": false
      },
      {
        "name": "timeout",
        "type": "number",
        "description": "Timeout in milliseconds (default: 30000)",
        "required": false
      }
    ]
  },
  {
    "name": "scan_media",
    "category": "Desktop",
    "description": "Scan a local directory for video, image, and audio files. Returns file paths, sizes, durations, resolution, and shooting session clusters. Use this to discover what media the user has available.",
    "params": [
      {
        "name": "directory",
        "type": "string",
        "description": "Directory path to scan (supports ~ for home)",
        "required": true
      },
      {
        "name": "type",
        "type": "string",
        "description": "Filter by media type (default: all)",
        "required": false,
        "enum": [
          "video",
          "image",
          "audio",
          "all"
        ]
      },
      {
        "name": "limit",
        "type": "number",
        "description": "Maximum files to return (default: 100)",
        "required": false
      }
    ]
  },
  {
    "name": "analyze_media",
    "category": "Desktop",
    "description": "Visual analysis of media files. Extracts frames at adjustable intervals, uploads them, and analyzes content. Control depth via frame_interval (300=survey, 30=detailed, 2=dense). Accepts 1-50 files with optional time windows.",
    "params": [
      {
        "name": "files",
        "type": "array",
        "description": "File path (supports ~)",
        "required": false
      },
      {
        "name": "frame_interval",
        "type": "number",
        "description": "Seconds between frames (300=fast sweep, 30=detailed, 2=dense)",
        "required": false
      },
      {
        "name": "focus",
        "type": "string",
        "description": "What to look for (e.g. 'best sunset shots', 'interview moments')",
        "required": false
      }
    ]
  },
  {
    "name": "upload_media",
    "category": "Desktop",
    "description": "Upload a local media file to onelight.video CDN storage. Returns a CDN URL and storageId. For videos, also extracts a representative frame. The URL can be used with import_clip.",
    "params": [
      {
        "name": "filePath",
        "type": "string",
        "description": "Path to the local file (supports ~)",
        "required": true
      },
      {
        "name": "extractFrame",
        "type": "boolean",
        "description": "For videos: extract a representative frame (default: true)",
        "required": false
      },
      {
        "name": "frameTimestamp",
        "type": "number",
        "description": "Timestamp in seconds for frame extraction (default: 25%)",
        "required": false
      }
    ]
  },
  {
    "name": "import_clip",
    "category": "Desktop",
    "description": "Import a local video file directly as a completed clip on the project timeline. Extracts a segment, uploads it, and creates a ready-to-play clip — no AI generation needed. Use when the user wants their actual footage in the project.",
    "params": [
      {
        "name": "workflowId",
        "type": "string",
        "description": "The project/workflow ID",
        "required": true
      },
      {
        "name": "filePath",
        "type": "string",
        "description": "Path to the local video file (supports ~)",
        "required": true
      },
      {
        "name": "title",
        "type": "string",
        "description": "Clip title",
        "required": false
      },
      {
        "name": "startSec",
        "type": "number",
        "description": "Start time in seconds (default: 0)",
        "required": false
      },
      {
        "name": "durationSec",
        "type": "number",
        "description": "Duration to extract in seconds (default: full file, max 30s)",
        "required": false
      },
      {
        "name": "orderIndex",
        "type": "number",
        "description": "Position in timeline (default: append)",
        "required": false
      }
    ]
  },
  {
    "name": "show_media_grid",
    "category": "Visual",
    "description": "Display an interactive thumbnail grid of media files in the chat. Use after scanning or when presenting specific files to the user. The UI renders thumbnails with hover-scrub for video. User can select items.",
    "params": [
      {
        "name": "items",
        "type": "array",
        "description": "Media items to display",
        "required": false,
        "enum": [
          "video",
          "image",
          "audio"
        ]
      },
      {
        "name": "selection_mode",
        "type": "string",
        "description": "Whether user can select items. Default: multi",
        "required": false,
        "enum": [
          "single",
          "multi",
          "none"
        ]
      },
      {
        "name": "label",
        "type": "string",
        "description": "Optional heading above the grid",
        "required": false
      }
    ]
  },
  {
    "name": "show_clusters",
    "category": "Visual",
    "description": "Display media grouped into temporal/spatial clusters as visual cards. Use when presenting scan results organized by shoot, date, or location. Each cluster shows a cover thumbnail, date range, and file count. User can select clusters to work with.",
    "params": [
      {
        "name": "clusters",
        "type": "array",
        "description": "Media clusters to display",
        "required": false
      },
      {
        "name": "label",
        "type": "string",
        "description": "Optional heading above the clusters",
        "required": true
      }
    ]
  },
  {
    "name": "show_folder_preview",
    "category": "Visual",
    "description": "Display a visual file browser rooted at a path. Shows an expandable folder tree with thumbnail previews and file counts. Use when showing the user what's in a specific directory.",
    "params": [
      {
        "name": "root_path",
        "type": "string",
        "description": "Root directory path",
        "required": false
      },
      {
        "name": "tree",
        "type": "array",
        "description": "Folder tree entries",
        "required": false,
        "enum": [
          "folder",
          "video",
          "image",
          "audio",
          "other"
        ]
      },
      {
        "name": "label",
        "type": "string",
        "description": "Optional heading",
        "required": false
      }
    ]
  },
  {
    "name": "complete_nux",
    "category": "NUX",
    "description": "Signal that the NUX onboarding flow is complete. Call this after the user has confirmed their creative direction and you've set the project title, narrative, and globalContext. This transitions the user from onboarding into their first project.",
    "params": [
      {
        "name": "title",
        "type": "string",
        "description": "Project title based on the chosen direction",
        "required": true
      },
      {
        "name": "narrative",
        "type": "string",
        "description": "Brief narrative/description for the project",
        "required": false
      }
    ]
  },
  {
    "name": "agent.tool_called",
    "category": "Other",
    "description": "0-3 short suggested replies",
    "params": []
  },
  {
    "name": "credits.limit_hit",
    "category": "Other",
    "description": "0-3 short suggested replies",
    "params": []
  },
  {
    "name": "clip.regenerated",
    "category": "Other",
    "description": "0-3 short suggested replies",
    "params": []
  },
  {
    "name": "upgrade_card.shown",
    "category": "Other",
    "description": "0-3 short suggested replies",
    "params": []
  },
  {
    "name": "agent.response_received",
    "category": "Other",
    "description": "0-3 short suggested replies",
    "params": []
  }
]
